import { useCallback, useEffect, useRef, useState } from 'react';
import Vimeo from '@u-wave/react-vimeo';
import type Player from '@vimeo/player';
import mixpanel from 'mixpanel-browser';
import { Box, ButtonBase } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { useMeCore } from '../../../core/modules/me';
import { SavedVideoPosition, VIDEO_POSITION_KEY } from '../../../core/utils/videoPosition';

const PROGRESS_MILESTONES = [25, 50, 75, 100];
const WATCHING_INTERVAL_MS = 60_000;
const END_THRESHOLD = 0.99;

// "Seguir donde quedé": la posición se guarda en el navegador del alumno (por usuario,
// clase y video). No se sincroniza entre dispositivos.
const RESUME_STORAGE_KEY = VIDEO_POSITION_KEY;
const RESUME_MIN_SECONDS = 10; // menos que esto no vale la pena retomar
const RESUME_FINISHED_RATIO = 0.95; // sobre esto se considera visto y parte de cero
const RESUME_REWIND_SECONDS = 3; // retroceder un poco para recuperar el contexto
const RESUME_SAVE_EVERY_SECONDS = 5;
const RESUME_NOTICE_MS = 8_000;

/** Hitos ya enviados en esta sesión del navegador, para no repetirlos por clase y video. */
const sentMilestones = new Set<string>();

type SavedPosition = SavedVideoPosition;

const readPosition = (key: string | null): SavedPosition | null => {
  if (!key) {
    return null;
  }
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as SavedPosition;
    return typeof parsed?.seconds === 'number' ? parsed : null;
  } catch {
    return null;
  }
};

const writePosition = (key: string | null, position: SavedPosition | null) => {
  if (!key) {
    return;
  }
  try {
    if (position) {
      localStorage.setItem(key, JSON.stringify(position));
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // Navegador privado o almacenamiento lleno: simplemente no se retoma.
  }
};

const isResumable = ({ seconds, duration }: SavedPosition) =>
  seconds >= RESUME_MIN_SECONDS && (!duration || seconds < duration * RESUME_FINISHED_RATIO);

const formatTime = (totalSeconds: number) => {
  const s = Math.floor(totalSeconds);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = String(s % 60).padStart(2, '0');
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
};

interface Props {
  videoId: string;
  url: string;
  classId: string;
  className: string;
  courseId?: string;
  courseName?: string;
  onPlaying?: () => void;
  /** Saltar a un segundo (desde el resumen de la clase). Cambia `nonce` para repetir el mismo salto. */
  seekTo?: { seconds: number; nonce: number };
}

const TrackedVimeo = ({
  videoId,
  url,
  classId,
  className,
  courseId,
  courseName,
  onPlaying,
  seekTo,
}: Props) => {
  const { user } = useMeCore();
  const positionRef = useRef(0);
  const durationRef = useRef(0);
  const lastSavedRef = useRef(0);
  const playerRef = useRef<Player | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pendingSeekRef = useRef<number | null>(null);
  const watchingTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTrackingEnabled = Boolean(process.env.REACT_APP_MIXPANEL_TOKEN);

  const storageKey = user?.id ? `${RESUME_STORAGE_KEY}:${user.id}:${classId}:${videoId}` : null;

  // Se calcula una sola vez al montar: el player aplica `start` solo al cargar el video.
  const [resumeFrom] = useState<number | undefined>(() => {
    const saved = readPosition(storageKey);
    return saved && isResumable(saved) ? Math.max(0, saved.seconds - RESUME_REWIND_SECONDS) : undefined;
  });
  const [showNotice, setShowNotice] = useState(resumeFrom !== undefined);

  const baseProps = useCallback(
    () => ({ classId, className, courseId, courseName }),
    [classId, className, courseId, courseName],
  );

  const savePosition = useCallback(() => {
    const seconds = positionRef.current;
    const duration = durationRef.current;
    if (!seconds) {
      return;
    }
    lastSavedRef.current = seconds;
    // savedAt permite que el Inicio sepa cuál fue la última clase vista
    writePosition(storageKey, isResumable({ seconds, duration }) ? { seconds, duration, savedAt: Date.now() } : null);
  }, [storageKey]);

  const trackMilestones = useCallback(
    (percent: number, duration: number) => {
      if (!isTrackingEnabled || !duration) {
        return;
      }

      PROGRESS_MILESTONES.forEach((milestone) => {
        const reached = milestone === 100 ? percent >= END_THRESHOLD : percent * 100 >= milestone;
        const key = `${classId}:${videoId}:${milestone}`;

        if (reached && !sentMilestones.has(key)) {
          sentMilestones.add(key);
          mixpanel.track('Video progress', {
            ...baseProps(),
            percent: milestone,
            videoDuration: Math.round(duration),
          });
        }
      });
    },
    [baseProps, classId, isTrackingEnabled, videoId],
  );

  const stopWatchingTimer = useCallback(() => {
    if (watchingTimer.current) {
      clearInterval(watchingTimer.current);
      watchingTimer.current = null;
    }
  }, []);

  const startWatchingTimer = useCallback(() => {
    if (!isTrackingEnabled || watchingTimer.current) {
      return;
    }

    watchingTimer.current = setInterval(() => {
      mixpanel.track('Video watching', {
        ...baseProps(),
        position: Math.round(positionRef.current),
      });
    }, WATCHING_INTERVAL_MS);
  }, [baseProps, isTrackingEnabled]);

  const hideNoticeLater = useCallback(() => {
    if (!noticeTimer.current) {
      noticeTimer.current = setTimeout(() => setShowNotice(false), RESUME_NOTICE_MS);
    }
  }, []);

  const restartFromBeginning = useCallback(() => {
    setShowNotice(false);
    positionRef.current = 0;
    writePosition(storageKey, null);
    playerRef.current?.setCurrentTime(0).catch(() => undefined);
  }, [storageKey]);

  const seekAndPlay = useCallback((player: Player, seconds: number) => {
    player
      .setCurrentTime(seconds)
      .then(() => player.play())
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!seekTo) {
      return;
    }
    setShowNotice(false);
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (playerRef.current) {
      seekAndPlay(playerRef.current, seekTo.seconds);
    } else {
      pendingSeekRef.current = seekTo.seconds;
    }
  }, [seekAndPlay, seekTo]);

  // Guardar también al cerrar la pestaña o salir de la clase.
  useEffect(() => {
    window.addEventListener('pagehide', savePosition);
    return () => {
      window.removeEventListener('pagehide', savePosition);
      savePosition();
    };
  }, [savePosition]);

  useEffect(
    () => () => {
      stopWatchingTimer();
      if (noticeTimer.current) {
        clearTimeout(noticeTimer.current);
      }
    },
    [stopWatchingTimer],
  );

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <Vimeo
        video={url}
        width="100%"
        start={resumeFrom}
        onReady={(player) => {
          playerRef.current = player;
          if (pendingSeekRef.current !== null) {
            seekAndPlay(player, pendingSeekRef.current);
            pendingSeekRef.current = null;
          }
        }}
        onPlay={startWatchingTimer}
        onPlaying={() => {
          startWatchingTimer();
          hideNoticeLater();
          onPlaying?.();
        }}
        onPause={() => {
          stopWatchingTimer();
          savePosition();
        }}
        onEnd={({ duration }) => {
          stopWatchingTimer();
          trackMilestones(1, duration);
          positionRef.current = 0;
          writePosition(storageKey, null);
        }}
        onTimeUpdate={({ seconds, percent, duration }) => {
          positionRef.current = seconds;
          durationRef.current = duration;
          trackMilestones(percent, duration);
          if (Math.abs(seconds - lastSavedRef.current) >= RESUME_SAVE_EVERY_SECONDS) {
            savePosition();
          }
        }}
        responsive
        speed
        controls
        playsInline
      />
      {showNotice && resumeFrom !== undefined ? (
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 8, md: 16 },
            left: { xs: 8, md: 16 },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 999,
            backgroundColor: 'rgba(15,23,42,0.85)',
            color: '#fff',
            fontSize: { xs: 12, md: 13 },
            boxShadow: '0 8px 24px -12px rgba(0,0,0,0.6)',
            zIndex: 1,
          }}
        >
          <span>Continuando desde {formatTime(resumeFrom + RESUME_REWIND_SECONDS)}</span>
          <ButtonBase
            onClick={restartFromBeginning}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: 999,
              color: '#c4b5fd',
              fontSize: 'inherit',
              fontWeight: 600,
              '&:hover': { backgroundColor: 'rgba(124,58,237,0.25)' },
            }}
          >
            <ReplayIcon sx={{ fontSize: 16 }} />
            Ver desde el inicio
          </ButtonBase>
        </Box>
      ) : null}
    </Box>
  );
};

export default TrackedVimeo;
